unit Essentials.Web.Attributes.Validation;

interface

uses
  Generics.Collections,
  Essentials.Shared,
  Data.DB;

type

/// <summary>
///   Enum containing validation failures for a proposed new attribute
/// </summary>
TValidationError = (
  /// <summary>
  ///   The TsxeDataAttribute.DataValue cannot be parsed into the type dictated
  ///   by TsxeDataAttributeDef.ValueKind
  /// </summary>
  ValueKindMismatch,
  /// <summary>
  ///   TsxeDataAttributeDef.MultiValue dictates that only one attribute of this
  ///   definition can appear on the entity and there already is one
  /// </summary>
  MultiValueViolation,
  /// <summary>
  ///   There is no TsxeDataAttributeDef found for this proposed attribute
  ///   (likely that it has not been selected in the UI)
  /// </summary>
  MissingAttributeType,
  /// <summary>
  ///   The combination of this TsxeDataAttribute.DataValue and
  ///   TsxeDataAttribute.AttributeId already exists on the entity (duplicate)
  /// </summary>
  AttributeAlreadyExists
);
TValidationErrorSet = set of TValidationError;

TAttributeDefinition = record
  ValueKind: TsxeValueType;
  MultiValue: Boolean;
  Name: string;
end;

TAttributeValue = record
  DataValue :string;
  Id: Integer;
end;

TAttributesValidator = class
private
  FAttributeIdToValues: TObjectDictionary<Integer, TList<TAttributeValue>>;
  FAttributeDefinitions: TDictionary<Integer, TAttributeDefinition>;
  /// <summary>
  ///   Handles determination of TValidationError.MultiValueViolation
  /// </summary>
  function ValidateInstanceOfDefinitionAlreadyExists(aId: Integer; ProposedTypeId: Integer): Boolean;
  /// <summary>
  ///   Handles determination of TValidationError.AttributeAlreadyExists
  /// </summary>
  function ValidateInstanceOfAttributeAlreadyExists(aId: Integer; ProposedTypeId: Integer; ProposedValue : string): Boolean;
  /// <summary>
  ///   Handles determination of TValidationError.ValueKindMismatch
  /// </summary>
  function ValidateValueType(ProposedValue: string; DefinitionValueType: TsxeValueType): Boolean;
  /// <remarks>
  ///   Takes ProposedTypeId as a string so that it can evaluate for
  ///   TValidationError.MissingAttributeType
  /// </remarks>
  procedure InternalValidate(aId: Integer; ProposedTypeId, ProposedValue: string; out ValidationErrors: TValidationErrorSet);
public
  constructor Create;
  destructor Destroy; override;
  /// <summary>
  ///   Public function to conduct validation and return boolean of success
  /// </summary>
  /// <param name="aId">
  ///   ID of the attribute being validated (use -1 if adding new data)
  /// </param>
  /// <param name="ProposedTypeId">
  ///   TsxeDataAttribute.AttributeId (or matching TsxeDataAttributeDef.Id)
  ///    <br />
  /// </param>
  /// <param name="ProposedValue">
  ///   TsxeDataAttribute.DataValue <br />
  /// </param>
  /// <param name="ValidationErrors">
  ///   TValidationError failures for this definition and attribute
  /// </param>
  /// <returns>
  ///   Validation Success (nothing in ValidationErrors)
  /// </returns>
  /// <remarks>
  ///   Calls InternalValidate and evaluates ValidationErrors
  /// </remarks>
  function Validate(aId: Integer; ProposedTypeId, ProposedValue: string; out ValidationErrors: TValidationErrorSet): Boolean;
  /// <summary>
  ///   Ensures internal data structure for attribute definitions is up-to-date.
  ///   Call after data is loaded in
  /// </summary>
  procedure RebuildAttributeDefinitions(aDataSet: TDataSet);
  /// <summary>
  ///   Ensures internal data structure for attributes is up-to-date. Call after
  ///   data is loaded in and if there are any additions / removals
  /// </summary>
  procedure RebuildAttributes(aDataSet: TDataSet);
//  procedure UpdateOrAddAttribute(aId, aAttributeId: Integer; aValue: string);
//  procedure RemoveAttribute(aId, aAttributeId: Integer);
  function GetValidationErrorDesc(aError: TValidationError; aAttributeType, aAttributeValue: string): string;
end;

const
  VALIDATION_ERROR_SCHEMAS: array[TValidationError] of string =
  (
  'Attribute type is %s but the value of %s cannot be recognised as %s',
  'Attribute definition only allows one %s per record',
  'Unknown attribute type',
  'There is already an attribute of %s with the value: %s'
  );

implementation

uses
SysUtils,
TypInfo,
Systematic.ObjectDictionary.Utils;

{ TAttributesValidator }

function TAttributesValidator.ValidateInstanceOfDefinitionAlreadyExists(aId: Integer; ProposedTypeId: Integer): Boolean;
var
  values: TList<TAttributeValue>;
begin
  //We already have an attribute with this definition ID
  if not FAttributeIdToValues.TryGetValue(ProposedTypeId, {o}values) then
    Exit(False);

  //if there's only 1 then make sure it's not the one we're editing
  if values.Count = 1 then
  begin
    Result := values[0].Id <> aId;
  end
  else //if 0 then false, if more than 1 then true
    Result := values.Count > 1;
end;

function TAttributesValidator.Validate(aId: Integer; ProposedTypeId, ProposedValue: string; out ValidationErrors: TValidationErrorSet): Boolean;
begin
  InternalValidate(aId, ProposedTypeId, ProposedValue, {o}ValidationErrors);
  Result := ValidationErrors = [];
end;

function TAttributesValidator.ValidateInstanceOfAttributeAlreadyExists(aId: Integer; ProposedTypeId: Integer; ProposedValue : string): Boolean;
var
  existingValues: TList<TAttributeValue>;
  existingValue: TAttributeValue;
begin
  if not FAttributeIdToValues.TryGetValue(ProposedTypeId, {o}existingValues) then
    Exit(False);

  for existingValue in existingValues do
  begin
    if existingValue.Id = aId then
      Continue;

    if existingValue.DataValue.Trim.ToUpper = ProposedValue.Trim.ToUpper then
      Exit(True);
  end;
  Result := False;
end;

function TAttributesValidator.ValidateValueType(ProposedValue: string; DefinitionValueType: TsxeValueType): Boolean;
var
  tempFlt: Extended;
  tempInt: Integer;
  tempDt: TDateTime;
begin
  case DefinitionValueType of
    TsxeValueType.Text : Result := True;
    TsxeValueType.Number : Result := TryStrToFloat(ProposedValue, {o}tempFlt);
    TsxeValueType.Integer : Result := TryStrToInt(ProposedValue, {o}tempInt);
    TsxeValueType.Date : Result := TryStrToDateTime(ProposedValue, {o}tempDt);
    else
      Result := False;
  end;
end;

//procedure TAttributesValidator.UpdateOrAddAttribute(aId, aAttributeId: Integer;
//  aValue: string);
//var
//  attVal, value: TAttributeValue;
//  values: TList<TAttributeValue>;
//  i: Integer;
//begin
//  //Editing not adding
//  if FAttributeIdToValues.TryGetValue(aAttributeId, {o}values) then
//  begin
//    for i := 0 to values.Count -1 do
//    begin
//      value := values[i];
//      if value.Id = aId then
//      begin
//        value.DataValue := aValue;
//        values[i] := value;
//        Exit;
//      end;
//    end;
//  end;
//
//  //Adding new
//  attVal.DataValue := aValue;
//  attVal.Id := aId;
//
//  TObDictUtils<Integer,TAttributeValue>.Add(
//    aAttributeId,
//    attVal,
//    FAttributeIdToValues
//  );
//end;

constructor TAttributesValidator.Create;
begin
  inherited;
  FAttributeIdToValues := TObjectDictionary<Integer, TList<TAttributeValue>>.Create;
  FAttributeDefinitions := TDictionary<Integer, TAttributeDefinition>.Create;
end;

destructor TAttributesValidator.Destroy;
begin
  FAttributeIdToValues.Free;
  FAttributeDefinitions.Free;
  inherited;
end;

function TAttributesValidator.GetValidationErrorDesc(aError: TValidationError;
  aAttributeType, aAttributeValue: string): string;
var
  definition: TAttributeDefinition;
  valuekindStr: string;
  attributeIdInt: Integer;
begin
  if not TryStrToInt(aAttributeType, {o}attributeIdInt) then
    Exit(VALIDATION_ERROR_SCHEMAS[MissingAttributeType]);

  definition  := FAttributeDefinitions[attributeIdInt];
  valueKindStr := GetEnumName(TypeInfo(TsxeValueType), Ord(definition.ValueKind));

  if aAttributeValue = '' then
    aAttributeValue := '[BLANK]';

  case aError of
    ValueKindMismatch : Result := Format(VALIDATION_ERROR_SCHEMAS[aError], [valueKindStr, QuotedStr(aAttributeValue), valueKindStr]);
    MultiValueViolation : Result := Format(VALIDATION_ERROR_SCHEMAS[aError], [QuotedStr(definition.Name)]);
    MissingAttributeType : Result := VALIDATION_ERROR_SCHEMAS[aError];
    AttributeAlreadyExists : Result := Format(VALIDATION_ERROR_SCHEMAS[aError], [QuotedStr(definition.Name), aAttributeValue]);
  end;
end;

procedure TAttributesValidator.InternalValidate(aId: Integer; ProposedTypeId, ProposedValue: string; out ValidationErrors: TValidationErrorSet);
var
  definition: TAttributeDefinition;
  proposedTypeIdInt: Integer;
begin
  ValidationErrors := [];

  if (not TryStrToInt(ProposedTypeId, {o}proposedTypeIdInt)) or
    (not FAttributeDefinitions.TryGetValue(proposedTypeIdInt, {o}definition)) then
  begin
    Include(ValidationErrors, MissingAttributeType);
    Exit;
  end;

  if not ValidateValueType(ProposedValue, definition.ValueKind) then
    Include(ValidationErrors, ValueKindMismatch);

  if (not definition.MultiValue) and ValidateInstanceOfDefinitionAlreadyExists(aId, proposedTypeIdInt) then
    Include(ValidationErrors, MultiValueViolation);

  if ValidateInstanceOfAttributeAlreadyExists(aId, proposedTypeIdInt, ProposedValue) then
    Include(ValidationErrors, AttributeAlreadyExists);
end;

procedure TAttributesValidator.RebuildAttributeDefinitions(aDataSet: TDataSet);
var
  attDef: TAttributeDefinition;
begin
  FAttributeDefinitions.Clear;
  aDataSet.First;
  while not aDataSet.Eof do
  begin
    attDef.ValueKind := TsxeValueType(GetEnumValue(TypeInfo(TsxeValueType), aDataSet.FieldByName('ValueKind').AsString));
    attDef.MultiValue := aDataSet.FieldByName('MultiValue').AsBoolean;
    attDef.Name := aDataSet.FieldByName('Name').AsString;
    FAttributeDefinitions.Add(aDataSet.FieldByName('Id').AsInteger, attDef);
    aDataSet.Next;
  end;
end;

procedure TAttributesValidator.RebuildAttributes(aDataSet: TDataSet);
var
  attVal: TAttributeValue;
begin
  FAttributeIdToValues.Clear;
  aDataSet.First;
  while not aDataSet.Eof do
  begin
    if aDataSet.FieldByName('DataValue').IsNull then
      attVal.DataValue := ''
    else
      attVal.DataValue := aDataSet.FieldByName('DataValue').AsString;
    attVal.Id := aDataSet.FieldByName('Id').AsInteger;

    TObDictUtils<Integer,TAttributeValue>.Add(
      aDataSet.FieldByName('AttributeId').AsInteger,
      attVal,
      FAttributeIdToValues
    );
    aDataSet.Next;
  end;
end;

//procedure TAttributesValidator.RemoveAttribute(aId, aAttributeId: Integer);
//var
//  value: TAttributeValue;
//  values: TList<TAttributeValue>;
//  i: Integer;
//begin
//  //Editing not adding
//  if FAttributeIdToValues.TryGetValue(aAttributeId, {o}values) then
//  begin
//    for i := values.Count -1 downto 0 do
//    begin
//      value := values[i];
//      if value.Id = aId then
//      begin
//        values.Remove(value);
//        Exit;
//      end;
//    end;
//  end;
//end;

end.
